import React, { memo } from 'react'
import { useConnect } from 'sb-redux-hook'
import actions from 'redux/actions'
import { constants, links, mixpanel, snowplow } from 'common/helpers'
import { FormattedMessage } from 'sb-react-intl'
import appConfig from 'app-config'

import CurrentUserAvatar from 'components/ui/CurrentUserAvatar/CurrentUserAvatar'
import DropMenu from 'layouts/InsideLayout/Header/HeaderNavDropMenu/HeaderNavDropMenu'
import HeaderNavItem from 'layouts/InsideLayout/Header/HeaderNavItem/HeaderNavItem'
import HeaderNavItemTitle from 'layouts/InsideLayout/Header/HeaderNavItemTitle/HeaderNavItemTitle'
import useCanUsePrepaidPlans from 'components/upgradePlans/useCanUsePrepaidPlans'


import messages from './messages'
import s from './UserNavItem.scss'


const HeaderUserNavItem = () => {
  const {
    me: { gender, avatar, firstName },
    isResubscribeVisible,
    isSubscriptionPaused,
    isSubscriptionUpgradable,
    isSubscriptionUndeliverable,
  } = useConnect({
    me: 'models.me',
    isSubscriptionPaused: 'models.subscription.isPaused',
    isSubscriptionUpgradable: 'models.subscription.isUpgradable',
    isSubscriptionUndeliverable: 'models.subscription.isUndeliverable',
  })

  const isPrepaidPlansLandingPageEnabled = useCanUsePrepaidPlans()

  const nav = [
    { title: messages.myQueue, to: links.queue, dataTestId: constants.testIds.headerUserNav.myQueue },
    { title: messages.tracking, to: links.tracking, dataTestId: constants.testIds.headerUserNav.tracking },
    (
      isSubscriptionUpgradable && (
        {
          title: messages.upgradeYourPlan,
          emojiIcon: '💰',
          dataTestId: constants.testIds.headerUserNav.upgradeYourPlan,
          onClick: () => {
            snowplow.track(constants.snowplowEvents.upgradeSubscriptionClick, { placement: 'user menu' }, false)
            actions.models.subscription.handleUpgradeClick({ placement: 'menu' })
          },
        }
      )
    ),
    (
      isPrepaidPlansLandingPageEnabled && (
        {
          title: messages.prepaidPlans,
          to: links.upgradePrepaid,
          emojiIcon: '🔥',
          dataTestId: constants.testIds.headerUserNav.upgradePrepaid,
        }
      )
    ),
    (
      isSubscriptionPaused && (
        {
          title: messages.unpauseSubscription,
          dataTestId: constants.testIds.headerUserNav.unpauseSubscription,
          onClick: () => {
            snowplow.track(constants.snowplowEvents.upgradeSubscriptionClick, { placement: 'user menu' }, false)
            actions.models.subscription.unpause({ placement: 'user menu' })
          },
        }
      )
    ),
    {
      title: messages.subscriptionStatus,
      to: links.profile.ordersStatus,
      dataTestId: constants.testIds.headerUserNav.subscriptionStatus,
    },
    (
      isResubscribeVisible && (
        {
          title: messages.resubscribe,
          dataTestId: constants.testIds.headerUserNav.resubscribe,
          onClick: () => {
            const resubscribeCode = constants.resubscribeTypes.resubnew

            snowplow.track(constants.snowplowEvents.resubscribeClick, {
              placement: 'user menu',
            }, false)

            if (isSubscriptionUndeliverable) {
              actions.models.router.push(`${links.subscribe}?resubscribeCode=${resubscribeCode}`)
            }
            else {
              actions.models.subscription.resubscribe({ popup: 'header user menu', resubscribeCode })
                .then(({ premiumProduct }) => {
                  if (premiumProduct) {
                    actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
                      item: premiumProduct,
                    })
                  }
                  else {
                    actions.models.modals.open(constants.modals.resubscribe.ResubscribeSuccess)
                  }
                }, () => {})
            }
          },
        }
      )
    ),
    { title: messages.sendGift, to: links.gift, dataTestId: constants.testIds.headerUserNav.sendGift },
    {
      to: links.invite,
      title: messages.inviteFriends,
      dataTestId: constants.testIds.headerUserNav.inviteFriends,
      onClick: () => {
        mixpanel.track(constants.analyticsEvents.shareLinkClick, {
          placement: 'desktop menu dropdown',
        })
      },
    },
    { title: messages.retakeQuiz, to: links.quiz, dataTestId: constants.testIds.headerUserNav.retakeQuiz },
    { title: messages.personalInfo, to: links.profile.personal, dataTestId: constants.testIds.headerUserNav.personalInfo },
    { title: messages.billingAndShipping, to: links.profile.billingAndShipping, dataTestId: constants.testIds.headerUserNav.billingAndShipping },
    { title: messages.changePassword, to: links.profile.password, dataTestId: constants.testIds.headerUserNav.changePassword },
    {
      title: messages.logout,
      id: 'logout',
      toOLD: links.logout,
      dataTestId: constants.testIds.headerUserNav.logout,
      onClick: (event) => {
        mixpanel.reset()
        actions.models.me.removeStorageData()

        if (process.env.LOCALTEST || process.env.WEBPACK === 'dev') {
          event.preventDefault()
          window.open(`${appConfig.services.api}logoff`)

          setTimeout(() => {
            window.location.pathname = links.login
          }, 500)
        }
      },
    },
  ].filter((item) => item).map((item) => {
    const { onClick, title, to, toOLD } = item

    return {
      ...item,
      onClick: (event) => {
        snowplow.track(constants.snowplowEvents.desktopProfileMenuClick, {
          title: title.en || title,
          link: to || toOLD,
        }, true)

        if (typeof onClick === 'function') {
          onClick(event)
        }
      },
    }
  })

  // TODO remove cdn url when avatar will be absolute path
  const avatarSrc = avatar || null

  return (
    <HeaderNavItem>
      <DropMenu items={nav} dataTestId="profileSubMenu">
        <div className={s.userNavItem}>
          <HeaderNavItemTitle className={s.name}>
            <FormattedMessage {...messages.navTitle} values={{ firstName }} />
          </HeaderNavItemTitle>
          <CurrentUserAvatar className={s.avatar} gender={gender} src={avatarSrc} circle alt="" />
          <div className={s.arrow} />
        </div>
      </DropMenu>
    </HeaderNavItem>
  )
}


export default memo(HeaderUserNavItem)
